import { DESCRIPTION_MAX, NAME_MAX } from '@littlebird/axon-mcp/contract';
import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { inputVariants } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Switch } from '@/components/ui/Switch';
import TextArea from '@/components/ui/TextArea';
import { electronApis } from '@/features/electron-api';
import {
  ArgRow,
  EditableList,
  FieldLabel,
  KeyValueRow,
  TestConnection,
  type ConnectionTestState,
} from '@/features/local-mcp';
import {
  useMcpServersQuery,
  useRefreshMcpServerMutation,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

import {
  addDraftRow,
  buildServerEntryChanges,
  createLocalServerDraft,
  removeDraftRow,
  toggleDraftRowReveal,
  updateDraftRowField,
  type LocalServerDraft,
} from './local-server-draft';
import { ServerSetupSection } from './server-setup-section';

import type { ConfigurationContext } from './configuration.types';
import type { ServerEntry } from '@littlebird/axon-mcp';

/** Accepts a plain draft or a `(prev) => next` updater, mirroring React's own
 * setState — the folder picker needs the updater form to avoid acting on a
 * stale draft while its async dialog is open. */
type SetLocalServerDraft = (
  update: LocalServerDraft | ((previous: LocalServerDraft) => LocalServerDraft)
) => void;

export function ConfigurationUnavailable({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <YStack className="items-center gap-3 p-10 text-center">
      <Icons.warn className="size-5 text-foreground-muted" />
      <p className="text-sm text-foreground-muted">{message}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </YStack>
  );
}

function rowFieldLabels(transportKind: LocalServerDraft['transportKind']) {
  return transportKind === 'http'
    ? {
        label: 'Headers',
        addLabel: 'Add a header',
        keyPlaceholder: 'Authorization',
      }
    : {
        label: 'Environment variables',
        addLabel: 'Add an environment variable',
        keyPlaceholder: 'KEY',
      };
}

function connectionTestState(
  server: ServerEntry,
  testing: boolean
): ConnectionTestState {
  if (testing) return 'testing';
  if (server.lastConnectionTest === undefined) return 'idle';
  return server.lastConnectionTest.ok ? 'success' : 'failure';
}

function LocalServerForm({
  server,
  draft,
  setDraft,
  saving,
  testing,
  onSave,
  onTest,
}: {
  server: ServerEntry;
  draft: LocalServerDraft;
  setDraft: SetLocalServerDraft;
  saving: boolean;
  testing: boolean;
  onSave: () => void;
  onTest: () => void;
}) {
  const rowLabels = rowFieldLabels(draft.transportKind);

  return (
    <YStack className="gap-6 p-6">
      <YStack className="gap-2">
        <XStack className="items-center justify-between">
          <FieldLabel>Name</FieldLabel>
          <span className="font-mono text-[10px] text-foreground-muted">
            {draft.name.length}/{NAME_MAX}
          </span>
        </XStack>
        <input
          aria-label="Name"
          value={draft.name}
          onChange={(event) =>
            setDraft({ ...draft, name: event.target.value.slice(0, NAME_MAX) })
          }
          maxLength={NAME_MAX}
          spellCheck={false}
          className={inputVariants()}
        />
      </YStack>

      <YStack className="gap-1.5">
        <XStack className="items-center justify-between">
          <FieldLabel optional>Description</FieldLabel>
          <span className="font-mono text-[10px] text-foreground-muted">
            {draft.description.length}/{DESCRIPTION_MAX}
          </span>
        </XStack>
        <TextArea
          aria-label="Description"
          value={draft.description}
          onChange={(event) =>
            setDraft({
              ...draft,
              description: event.target.value.slice(0, DESCRIPTION_MAX),
            })
          }
          maxLength={DESCRIPTION_MAX}
          spellCheck={false}
          className="min-h-[64px] resize-y text-sm"
        />
      </YStack>

      <XStack className="items-center justify-between rounded-lg border border-border p-3">
        <YStack className="gap-0.5">
          <span className="text-sm font-medium text-foreground">Enabled</span>
          <span className="text-xs text-foreground-muted">
            Turn this server off without removing it
          </span>
        </YStack>
        <Switch
          aria-label="Enabled"
          checked={draft.enabled !== false}
          onCheckedChange={(checked) =>
            setDraft({ ...draft, enabled: checked })
          }
        />
      </XStack>

      <EditableList
        label={rowLabels.label}
        addLabel={rowLabels.addLabel}
        onAdd={() => setDraft(addDraftRow(draft))}
      >
        {draft.maskedRows.map((row) => (
          <KeyValueRow
            key={row.id}
            keyValue={row.key}
            value={draft.rowValues[row.id] ?? ''}
            onKeyChange={(value) =>
              setDraft(updateDraftRowField(draft, row.id, 'key', value))
            }
            onValueChange={(value) =>
              setDraft(updateDraftRowField(draft, row.id, 'value', value))
            }
            onRemove={() => setDraft(removeDraftRow(draft, row.id))}
            keyPlaceholder={rowLabels.keyPlaceholder}
            valuePlaceholder="value"
            keyWidthClass="w-[38%]"
            secret={{
              reveal: row.reveal,
              onToggle: () => setDraft(toggleDraftRowReveal(draft, row.id)),
            }}
          />
        ))}
      </EditableList>

      {draft.transportKind !== 'http' && (
        <EditableList
          label="Arguments"
          addLabel="Add an argument"
          onAdd={() => setDraft({ ...draft, args: [...draft.args, ''] })}
        >
          {draft.args.map((value, index) => (
            <ArgRow
              key={index}
              value={value}
              onChange={(next) =>
                setDraft({
                  ...draft,
                  args: draft.args.map((arg, i) => (i === index ? next : arg)),
                })
              }
              onPickFolder={() =>
                void electronApis.axonMcp.pickDirectory().then((dir) => {
                  if (!dir) return;
                  setDraft((previous) => ({
                    ...previous,
                    args: previous.args.map((arg, i) =>
                      i === index ? dir : arg
                    ),
                  }));
                })
              }
              onRemove={() =>
                setDraft({
                  ...draft,
                  args: draft.args.filter((_, i) => i !== index),
                })
              }
            />
          ))}
        </EditableList>
      )}

      <TestConnection
        test={connectionTestState(server, testing)}
        canTest
        serverName={server.lastConnectionTest?.serverName ?? server.name}
        toolCount={server.lastConnectionTest?.toolCount ?? 0}
        errorText="The server didn't respond to the last test."
        onTest={onTest}
      />

      <ServerSetupSection server={server} />

      <XStack className="items-center justify-end border-t border-border pt-4">
        <Button
          variant="default"
          size="sm"
          disabled={saving}
          isLoading={saving}
          onClick={onSave}
        >
          Save settings
        </Button>
      </XStack>
    </YStack>
  );
}

export function LocalServerConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const serverSlug =
    context.localSource?.kind === 'local_mcp'
      ? context.localSource.serverSlug
      : undefined;

  const serversQuery = useMcpServersQuery();
  const update = useUpdateMcpServerMutation();
  const refresh = useRefreshMcpServerMutation();

  const server = serversQuery.data?.find(
    (candidate) => candidate.slug === serverSlug
  );

  // Seeds the draft once, the first time this server's data arrives, and
  // never again for the same slug — later `server` object changes (a
  // successful or failed save) must not overwrite in-progress edits. A fresh
  // mount (e.g. re-selecting this provider) gets a fresh ref, so it re-seeds
  // from whatever the query cache currently holds.
  const [draft, setDraft] = useState<LocalServerDraft | null>(null);
  const initializedSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (server && initializedSlugRef.current !== server.slug) {
      initializedSlugRef.current = server.slug;
      setDraft(createLocalServerDraft(server));
    }
  }, [server]);

  if (serversQuery.isError) {
    return (
      <ConfigurationUnavailable
        message="Couldn't read this server from Littlebird's local settings."
        onRetry={() => void serversQuery.refetch()}
      />
    );
  }
  if (serversQuery.isPending) return null;
  if (!server || !draft) {
    return (
      <ConfigurationUnavailable
        message="This server is no longer available."
        onRetry={() => void serversQuery.refetch()}
      />
    );
  }

  const setNonNullDraft: SetLocalServerDraft = (next) => {
    setDraft((previous) => {
      if (!previous) return previous;
      return typeof next === 'function' ? next(previous) : next;
    });
  };

  return (
    <LocalServerForm
      server={server}
      draft={draft}
      setDraft={setNonNullDraft}
      saving={update.isPending}
      testing={refresh.isPending}
      onSave={() =>
        update.mutate({
          slug: server.slug,
          changes: buildServerEntryChanges(server, draft),
        })
      }
      onTest={() => refresh.mutate(server)}
    />
  );
}
